<template>
  <div class="page-overlay" :style="overlayStyle">
    <div class="page-overlay__header">
      <div class="page-overlay__left">
        <span class="page-overlay__back" @click="$emit('close')">
          <a-icon type="arrow-left" />
          <span>返回</span>
        </span>
        <div class="page-overlay__title">
          <slot name="title"></slot>
        </div>
      </div>
      <span class="page-overlay__close" @click="$emit('close')">
        <a-icon type="close" />
      </span>
    </div>
    <div class="page-overlay__body">
      <slot></slot>
    </div>
  </div>
</template>

<script>
/**
 * PageOverlay 覆盖式二级页面壳（公共组件）
 *
 * 用途：不跳路由，把二级页面覆盖到一级页面的同一区域上——铺满父容器
 * （通常是页面根节点），不遮挡全局侧边栏/顶栏。适用于「列表 → 管理/详情」
 * 的下钻场景，覆盖层内可继续叠 Modal / Drawer。
 *
 * 用法：作为容器元素的直接子组件挂载，用 v-if 控制显隐：
 *   <page-overlay @close="visible = false">
 *     <template slot="title">标题<span>可放 tag 等任意内容</span></template>
 *     页面内容
 *   </page-overlay>
 *
 * 滚动：覆盖层随页面一起上下滚动（与一级页面共用一条滚动条），
 * 头部「返回 / X」吸顶，滚动过程中始终可达。
 *
 * 顶部对齐：自动补偿同级元素负外边距向上溢出的高度（如 ds-header 的
 * margin-top: -20px），保证覆盖层顶满内容区顶部、不露边。
 *
 * 实现：覆盖层为 absolute 铺满父容器，父容器由组件自动加上
 * page-overlay-anchor（position: relative）作为定位锚点，卸载时移除；
 * 嵌套挂在上级覆盖层内时自动改锚定到上级覆盖层根节点，实现逐层整层覆盖。
 */
export default {
  name: "PageOverlay",
  props: {
    // 覆盖层 z-index，默认 100：高于页面内容、低于 antd 弹窗（1000）
    zIndex: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      topOffset: 0,
    };
  },
  computed: {
    overlayStyle() {
      return {
        zIndex: this.zIndex,
        top: `${this.topOffset}px`,
      };
    },
  },
  mounted() {
    const parent = this.$el && this.$el.parentElement;
    // 嵌套场景（如 文档管理 → 分块管理）：自身挂在上级覆盖层的内容区内时，
    // 不加锚点也不移动 DOM——absolute 元素会自动向上穿透非定位祖先，直接
    // 锚定到上级覆盖层根（自身为 positioned），实现整层覆盖。
    // 切勿 appendChild 移动 DOM：会破坏 Vue 2 的插槽 patch 锚点，导致
    // 关闭后再次打开时组件静默不渲染。
    if (parent && !parent.closest(".page-overlay")) {
      parent.classList.add("page-overlay-anchor");
      this.measureTopOffset();
    } else {
      // 嵌套时直接覆盖上级整个覆盖层，无顶部偏移
      this.topOffset = 0;
    }
    // 打开覆盖层时滚动条归位到顶部，保证覆盖层从头展示
    this.scrollTopToTop();
    window.addEventListener("resize", this.measureTopOffset);
  },
  beforeDestroy() {
    const parent = this.$el && this.$el.parentElement;
    if (parent && !parent.classList.contains("page-overlay")) {
      parent.classList.remove("page-overlay-anchor");
    }
    window.removeEventListener("resize", this.measureTopOffset);
  },
  methods: {
    // 向上查找真实滚动容器（宿主布局的滚动容器不一定是 body，如 el-main）
    getScrollParent() {
      let el = this.$el && this.$el.parentElement;
      while (el && el !== document.documentElement) {
        const cs = getComputedStyle(el);
        if (/(auto|scroll)/.test(cs.overflowY)) return el;
        el = el.parentElement;
      }
      return null;
    },
    scrollTopToTop() {
      const scroller = this.getScrollParent();
      if (scroller) {
        scroller.scrollTop = 0;
      } else {
        window.scrollTo(0, 0);
      }
    },
    // 取同级可见元素相对父容器顶边的最小偏移（负值即向上溢出，如 ds-header 的 -20px），
    // 覆盖层顶部随之上移，避免溢出部分露在覆盖层上方（仅非嵌套场景需要）
    measureTopOffset() {
      const parent = this.$el && this.$el.parentElement;
      if (!parent || parent.closest(".page-overlay")) return;
      const parentTop = parent.getBoundingClientRect().top;
      let offset = 0;
      Array.prototype.forEach.call(parent.children, (el) => {
        if (el === this.$el || (!el.offsetWidth && !el.offsetHeight)) return;
        const top = el.getBoundingClientRect().top - parentTop;
        if (top < offset) offset = top;
      });
      this.topOffset = Math.round(offset);
    },
  },
};
</script>

<style lang="scss">
/* 锚点类加在父容器上（无本组件的 scoped 标记），需全局样式 */
.page-overlay-anchor {
  position: relative;
}
</style>

<style scoped lang="scss">
.page-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  // 不锁 bottom：高度 min-height 撑底、内容更多时自身长高——
  // absolute 元素超出部分计入文档滚动高度，二级内容比一级页面高时仍可滚到底
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  animation: page-overlay-in 0.22s ease;

  .page-overlay__header {
    position: sticky;
    top: -20px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    flex-shrink: 0;
    padding: 0 24px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
  }

  .page-overlay__left {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }

  .page-overlay__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.65);
    font-size: 13px;

    .anticon {
      font-size: 15px;
    }

    &:hover {
      color: #1890ff;
    }
  }

  .page-overlay__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.45);
    font-size: 16px;
    border-radius: 4px;

    &:hover {
      color: rgba(0, 0, 0, 0.85);
      background: rgba(0, 0, 0, 0.04);
    }
  }

  .page-overlay__title {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    font-size: 15px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }

  // 内容区不设自身滚动，跟随页面滚动条
  .page-overlay__body {
    flex: 1;
    padding: 20px 24px 32px;
  }
}

@keyframes page-overlay-in {
  from {
    transform: translateX(24px);
    opacity: 0;
  }

  to {
    transform: none;
    opacity: 1;
  }
}
</style>
